import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, RefreshCw, Scale, CheckCircle2, AlertTriangle, Info } from 'lucide-react';
import { toast } from 'sonner';

interface CapTableReconciliationProps {
  companyId: string;
}

interface ScenarioSnapshot {
  id: string;
  name: string;
  scenario_id?: string;
}

interface Delta {
  holder: string;
  share_class: string;
  actual_pct: number;
  modeled_pct: number;
  delta_pct: number;
  delta_shares: number;
  actual_shares: number;
  modeled_shares: number;
}

export function CapTableReconciliation({ companyId }: CapTableReconciliationProps) {
  const [scenarios, setScenarios] = useState<ScenarioSnapshot[]>([]);
  const [selected, setSelected] = useState('');
  const [diffs, setDiffs] = useState<Delta[]>([]);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);

  async function load() {
    setLoading(true);
    try {
      console.log('CapTableReconciliation: Loading scenarios for company:', companyId);

      // Query funding_rounds with state_type PROJECTION to get projected scenarios
      const { data: s, error } = await supabase
        .from('funding_rounds')
        .select('id, name, round_label, scenario_id, state_type')
        .eq('company_id', companyId)
        .in('state_type', ['PROJECTION', 'SCENARIO'])
        .order('created_at', { ascending: false });

      console.log('CapTableReconciliation: Query result:', { data: s, error });

      if (error) throw error;

      // Map to expected format, using the unique funding_round id
      const mappedScenarios = (s || []).map((round) => ({
        id: round.id, // Use the funding_round id as the unique identifier
        name: round.round_label || round.name || `${round.state_type} - ${round.id.slice(0, 8)}`,
        scenario_id: round.scenario_id, // Keep scenario_id for reconciliation
      }));

      console.log('CapTableReconciliation: Mapped scenarios:', mappedScenarios);

      setScenarios(mappedScenarios);

      if (mappedScenarios.length > 0) {
        toast.success(`Found ${mappedScenarios.length} scenario(s)`);
      }
    } catch (e: any) {
      console.error('CapTableReconciliation: Load error:', e);
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function reconcile() {
    if (!selected) {
      toast.error('Please select a scenario to reconcile');
      return;
    }

    // Find the selected scenario to get its scenario_id
    const selectedScenario = scenarios.find((s) => s.id === selected);
    const scenarioIdToUse = selectedScenario?.scenario_id || selected;

    setReconciling(true);
    try {
      const { data, error } = await supabase.functions.invoke('reconcile-cap-tables', {
        body: { companyId, scenarioId: scenarioIdToUse },
      });
      if (error) throw error;
      setDiffs(data?.deltas || []);

      const significantDiffs =
        data?.deltas?.filter((d: Delta) => Math.abs(d.delta_pct) > 0.01).length || 0;

      if (data?.mismatch) {
        toast.warning(`Cap table mismatch: ${significantDiffs} significant differences found`);
      } else {
        toast.success('Cap table reconciled successfully - no significant differences');
      }
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setReconciling(false);
    }
  }

  useEffect(() => {
    load();
  }, [companyId]);

  return (
    <Card className="border-border bg-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Scale className="text-primary h-5 w-5" /> Cap Table Reconciliation
          </CardTitle>
          <CardDescription>
            Compare your actual cap table against simulated scenarios
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select Scenario" />
            </SelectTrigger>
            <SelectContent>
              {scenarios.length === 0 ? (
                <SelectItem value="none" disabled>
                  No scenarios available
                </SelectItem>
              ) : (
                scenarios.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            onClick={load}
            disabled={loading}
            title="Refresh scenario list"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button onClick={reconcile} disabled={reconciling || !selected}>
            {reconciling ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Reconciling...
              </>
            ) : (
              <>
                <Scale className="h-4 w-4 mr-2" />
                Reconcile
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="animate-spin text-muted-foreground" />
          </div>
        ) : scenarios.length === 0 ? (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              No saved scenarios found. Run a simulation in the Scenario Workbench above to create
              scenarios for reconciliation.
            </AlertDescription>
          </Alert>
        ) : diffs.length === 0 ? (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Select a scenario from the dropdown and click "Reconcile" to compare it against your
              actual cap table.
            </AlertDescription>
          </Alert>
        ) : (
          <>
            {diffs.some((d) => Math.abs(d.delta_pct) > 1) && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Significant differences detected.</strong> Some holdings differ by more
                  than 1%.
                </AlertDescription>
              </Alert>
            )}

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Holder</TableHead>
                    <TableHead>Class</TableHead>
                    <TableHead className="text-right">Actual Shares</TableHead>
                    <TableHead className="text-right">Actual %</TableHead>
                    <TableHead className="text-right">Modeled Shares</TableHead>
                    <TableHead className="text-right">Modeled %</TableHead>
                    <TableHead className="text-right">Δ Shares</TableHead>
                    <TableHead className="text-right">Δ %</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {diffs.map((d) => (
                    <TableRow key={`${d.holder}-${d.share_class}`}>
                      <TableCell className="font-medium">{d.holder}</TableCell>
                      <TableCell>{d.share_class}</TableCell>
                      <TableCell className="text-right">{num(d.actual_shares)}</TableCell>
                      <TableCell className="text-right">{pct(d.actual_pct)}</TableCell>
                      <TableCell className="text-right">{num(d.modeled_shares)}</TableCell>
                      <TableCell className="text-right">{pct(d.modeled_pct)}</TableCell>
                      <TableCell className="text-right">{num(d.delta_shares)}</TableCell>
                      <TableCell className="text-right">
                        <span
                          className={
                            Math.abs(d.delta_pct) > 0.01
                              ? 'text-amber-600 font-medium'
                              : 'text-muted-foreground'
                          }
                        >
                          {pct(d.delta_pct)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {Math.abs(d.delta_pct) < 0.01 ? (
                          <CheckCircle2 className="text-green-600 h-4 w-4" />
                        ) : (
                          <AlertTriangle className="text-amber-500 h-4 w-4" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function pct(n: any) {
  if (n == null) return '—';
  return `${Number(n).toFixed(2)}%`;
}

function num(n: any) {
  if (n == null) return '—';
  return Number(n).toLocaleString();
}
